import { useEffect, useRef, useState } from "react";
import { useAuthState } from "../../../auth/AuthStateProvider";
import { logConnectorFailure, logConnectorSuccess } from "../../../telemetry";
import { readInvite, type InviteContent } from "../domain/agenda";
import { fetchAttachments, fetchEventBody, fetchRecurrenceLabel } from "../services/eventDetail";
import type { AttachmentInfo, CalEvent } from "../types";

interface EventDetailState {
  /** null while loading; [] when there are none or they couldn't be read. */
  readonly attachments: readonly AttachmentInfo[] | null;
  /** "Repeats weekly"; undefined while loading, null when not recurring or the pattern couldn't be read. */
  readonly recurrence: string | null | undefined;
}

interface InviteState {
  /** null while loading, or when the body couldn't be read (`failed`). */
  readonly invite: InviteContent | null;
  readonly failed: boolean;
}

/**
 * Extra detail for the meeting screen that calendarView doesn't return:
 * attachments, the repeat pattern and the full invite body (split into its
 * agenda block and its own text). The body is read again whenever the event
 * changes in Graph, so a saved agenda shows once it lands.
 */
export function useEventDetail(token: string | null, event: CalEvent | null): EventDetailState & InviteState {
  const { refreshGraphToken } = useAuthState();
  const [state, setState] = useState<EventDetailState>({ attachments: null, recurrence: undefined });
  const [invite, setInvite] = useState<InviteState>({ invite: null, failed: false });
  const tokenRef = useRef(token);
  tokenRef.current = token;

  const eventId = event?.id ?? null;
  const hasAttachments = event?.hasAttachments ?? false;
  const seriesMasterId = event?.seriesMasterId ?? null;
  const changeKey = event?.changeKey ?? "";

  useEffect(() => {
    const current = tokenRef.current;
    const readsRepeat = Boolean(current && eventId && seriesMasterId);
    setState({ attachments: hasAttachments ? null : [], recurrence: readsRepeat ? undefined : null });
    if (!current || !eventId) return;

    let cancelled = false;
    const refresh = () => refreshGraphToken(false);

    if (hasAttachments) {
      fetchAttachments(current, refresh, eventId)
        .then((attachments) => {
          if (cancelled) return;
          setState((s) => ({ ...s, attachments }));
          logConnectorSuccess("calendar", "load-attachments");
        })
        .catch((e: unknown) => {
          if (cancelled) return;
          setState((s) => ({ ...s, attachments: [] }));
          logConnectorFailure("calendar", e, "load-attachments");
        });
    }

    if (seriesMasterId) {
      fetchRecurrenceLabel(current, refresh, seriesMasterId)
        .then((recurrence) => {
          if (!cancelled) setState((s) => ({ ...s, recurrence }));
        })
        .catch((e: unknown) => {
          if (cancelled) return;
          setState((s) => ({ ...s, recurrence: null }));
          logConnectorFailure("calendar", e, "load-recurrence");
        });
    }

    return () => {
      cancelled = true;
    };
  }, [eventId, hasAttachments, seriesMasterId, refreshGraphToken]);

  // Another meeting starts from blank; the same meeting keeps what it shows until the new body arrives.
  useEffect(() => {
    setInvite({ invite: null, failed: false });
  }, [eventId]);

  useEffect(() => {
    const current = tokenRef.current;
    if (!current || !eventId) return;
    // Pane-only events (a focus block not yet sent) have no body in Graph.
    if (eventId.startsWith("local-")) {
      setInvite({ invite: { agenda: null, text: "" }, failed: false });
      return;
    }
    let cancelled = false;
    fetchEventBody(current, () => refreshGraphToken(false), eventId)
      .then((html) => {
        if (!cancelled) setInvite({ invite: readInvite(html), failed: false });
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        setInvite({ invite: null, failed: true });
        logConnectorFailure("calendar", e, "load-body");
      });
    return () => {
      cancelled = true;
    };
  }, [eventId, changeKey, refreshGraphToken]);

  return { ...state, ...invite };
}
